<script setup lang="ts">
import { computed, provide } from 'vue'
import BlockRenderer from './BlockRenderer.vue'
import { websiteEditableContextKey } from './editable-context'
import { websiteNavigationContextKey } from './website-navigation-context'
import { serviceAreaContextKey } from './service-area-context'
import { formsContextKey } from './forms-context'
import type { WebsiteLinkSelection } from './editable-context'
import { getTheme, themeTokensToStyle } from './themes'
import type {
  WebsiteBlueprint,
  WebsiteFormCaptureState,
  WebsiteFormSubmission,
  WebsiteLeadCaptureState,
  WebsiteLeadSubmission,
} from './types'

const props = withDefaults(
  defineProps<{
    blueprint: WebsiteBlueprint
    pageSlug?: string
    editable?: boolean
    leadCapture?: WebsiteLeadCaptureState
    formCapture?: WebsiteFormCaptureState
  }>(),
  { editable: false },
)
const emit = defineEmits<{
  contentChange: []
  linkSelect: [selection: WebsiteLinkSelection]
  leadSubmit: [submission: WebsiteLeadSubmission]
  formSubmit: [submission: WebsiteFormSubmission]
}>()

const theme = computed(() => getTheme(props.blueprint.themeId))
const themeVariables = computed(() => {
  const style = themeTokensToStyle(theme.value.tokens)
  const heading = props.blueprint.typography?.heading
  const body = props.blueprint.typography?.body
  const wordmark = props.blueprint.typography?.wordmark

  if (heading) style['--site-font-heading'] = `"${heading.family.replaceAll('"', '')}", ${heading.category}`
  if (body) style['--site-font-body'] = `"${body.family.replaceAll('"', '')}", ${body.category}`

  style['--site-font-wordmark'] = wordmark ? `"${wordmark.family.replaceAll('"', '')}", ${wordmark.category}` : 'var(--site-font-heading)'
  style['--site-logo-direction'] = 'row'
  if (props.blueprint.brand && 'logoWidth' in props.blueprint.brand) {
    const width = props.blueprint.brand.logoWidth
    const custom = typeof width === 'number' && width > 0 ? `${width}px` : null
    style['--site-logo-width'] = custom ?? '170px'
    style['--site-symbol-width'] = custom ?? '40px'
  }

  return style
})
const page = computed(() =>
  props.blueprint.pages.find((candidate) => candidate.slug === props.pageSlug) ?? props.blueprint.pages[0],
)
const navigations = computed(() => new Map(
  (props.blueprint.navigations ?? []).map((navigation) => [navigation.id, navigation]),
))
const serviceAreaSets = computed(() => new Map(
  (props.blueprint.serviceAreaSets ?? []).map((set) => [set.id, set]),
))
const forms = computed(() => new Map(
  (props.blueprint.forms ?? []).map((form) => [form.id, form]),
))

provide(websiteEditableContextKey, {
  editable: computed(() => props.editable),
  contentChanged: () => emit('contentChange'),
  linkSelected: (selection) => emit('linkSelect', selection),
})
provide(websiteNavigationContextKey, { navigations })
provide(serviceAreaContextKey, { sets: serviceAreaSets })
provide(formsContextKey, { forms })
</script>

<template>
  <div
    class="site-root"
    :data-editable="editable || undefined"
    :data-theme-id="theme.id"
    :data-theme-name="theme.name"
    :style="themeVariables"
  >
    <slot :blueprint="blueprint" :page="page">
      <main v-if="page">
        <BlockRenderer
          v-for="block in page.blocks"
          :key="block.id"
          :block="block"
          :lead-capture="leadCapture"
          :form-capture="formCapture"
          @lead-submit="emit('leadSubmit', $event)"
          @form-submit="emit('formSubmit', $event)"
        />
      </main>
      <main v-else class="site-site-container site:py-16 site:text-center">
        This website does not have a page yet.
      </main>
    </slot>
  </div>
</template>
